/**
 * Jobs Page
 * Main page for browsing and searching job listings
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { Checkbox } from '../components/ui/checkbox';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../components/ui/sheet';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import {
  Search,
  MapPin,
  Briefcase,
  Clock,
  DollarSign,
  Filter,
  ChevronRight,
  Star,
  Loader2,
  Building2,
  Calendar,
  Eye,
  Users,
  Zap,
  Home,
  Laptop,
  SlidersHorizontal,
  X,
  RefreshCw,
  Bookmark,
  BookmarkPlus,
  Bell,
  BellOff,
  Trash2,
  ChevronDown,
  Play,
  MoreVertical,
  Package,
} from 'lucide-react';
import { useCountry } from '../context/CountryContext';
import { ITServicesTopSection, ITServicesInlineCard } from '../components/services/ITServicesSection';
import JobCareerServicesSection from '../components/services/JobCareerServicesSection';
import { JobServicePurchaseSheet } from '../components/services/JobServicePurchase';
import { useToast } from '../hooks/use-toast';
import { useServiceNudges } from '../hooks/useServiceNudges';
import { NudgeBanner, InlineNudgeCard, NudgeToast } from '../components/ServiceNudge';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Work style icons
const WORK_STYLE_ICONS = {
  'on-site': Building2,
  'remote': Laptop,
  'hybrid': Home,
};

// Job Card Component
const JobCard = ({ job, currency, onClick }) => {
  const WorkStyleIcon = WORK_STYLE_ICONS[job.work_style] || Building2;
  
  const formatSalary = () => {
    if (!job.salary_min && !job.salary_max) return 'Negotiable';
    if (job.salary_min && job.salary_max) {
      if (job.salary_min === job.salary_max) {
        return `${currency.symbol} ${job.salary_min.toLocaleString()}`;
      }
      return `${currency.symbol} ${job.salary_min.toLocaleString()} - ${job.salary_max.toLocaleString()}`;
    }
    if (job.salary_min) return `From ${currency.symbol} ${job.salary_min.toLocaleString()}`;
    return `Up to ${currency.symbol} ${job.salary_max.toLocaleString()}`;
  };

  const getPayPeriod = () => {
    const periods = {
      'hourly': '/hour',
      'daily': '/day',
      'weekly': '/week',
      'monthly': '/month',
      'project': '/project',
    };
    return periods[job.pay_type] || '/month';
  };

  return (
    <Card 
      className="hover:shadow-md transition-all cursor-pointer border-l-4 border-l-transparent hover:border-l-blue-500"
      onClick={onClick}
      data-testid={`job-card-${job.id}`}
    >
      <CardContent className="p-4">
        <div className="flex justify-between items-start gap-4">
          <div className="flex-1 min-w-0">
            {/* Company & Badges */}
            <div className="flex items-center gap-2 mb-1">
              <span className="text-sm text-gray-500 truncate">{job.company_name}</span>
              {job.featured && (
                <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-xs">
                  <Star className="h-3 w-3 mr-1" />
                  Featured
                </Badge>
              )}
              {job.urgent && (
                <Badge className="bg-red-100 text-red-700 border-red-200 text-xs">
                  <Zap className="h-3 w-3 mr-1" />
                  Urgent
                </Badge>
              )}
            </div>
            
            {/* Job Title */}
            <h3 className="font-semibold text-gray-900 mb-2 line-clamp-1">{job.title}</h3>
            
            {/* Meta Info */}
            <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500">
              <span className="flex items-center gap-1">
                <MapPin className="h-3.5 w-3.5" />
                {job.location}
              </span>
              <span className="flex items-center gap-1">
                <WorkStyleIcon className="h-3.5 w-3.5" />
                {job.work_style === 'on-site' ? 'On-site' : job.work_style === 'remote' ? 'Remote' : 'Hybrid'}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="h-3.5 w-3.5" />
                {job.employment_type === 'full-time' ? 'Full-time' : 
                 job.employment_type === 'part-time' ? 'Part-time' : 
                 job.employment_type === 'contract' ? 'Contract' : 
                 job.employment_type}
              </span>
            </div>
            
            {/* Skills */}
            {job.skills_required && job.skills_required.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {job.skills_required.slice(0, 3).map((skill, idx) => (
                  <Badge key={idx} variant="outline" className="text-xs bg-gray-50">
                    {skill}
                  </Badge>
                ))}
                {job.skills_required.length > 3 && (
                  <Badge variant="outline" className="text-xs bg-gray-50">
                    +{job.skills_required.length - 3}
                  </Badge>
                )}
              </div>
            )}
          </div>
          
          {/* Salary & Stats */}
          <div className="text-right flex-shrink-0">
            <div className="text-lg font-bold text-green-600">
              {formatSalary()}
            </div>
            <div className="text-xs text-gray-400">{getPayPeriod()}</div>
            <div className="flex items-center justify-end gap-2 mt-2 text-xs text-gray-400">
              <span className="flex items-center gap-1">
                <Eye className="h-3 w-3" />
                {job.views || 0}
              </span>
              <span className="flex items-center gap-1">
                <Users className="h-3 w-3" />
                {job.applications_count || 0}
              </span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Filter Sidebar Component - Enhanced with salary range, location, and date filters
const FilterSidebar = ({ 
  filters, 
  selectedFilters, 
  onFilterChange, 
  onClear, 
  isMobile = false,
  salaryRange,
  onSalaryChange,
  locationFilter,
  onLocationChange,
  postedWithin,
  onPostedWithinChange,
  currency
}) => {
  const [salaryMin, setSalaryMin] = useState(salaryRange?.min || '');
  const [salaryMax, setSalaryMax] = useState(salaryRange?.max || '');

  const handleSalaryApply = () => {
    onSalaryChange({
      min: salaryMin ? parseInt(salaryMin) : null,
      max: salaryMax ? parseInt(salaryMax) : null
    });
  };

  const renderFilterGroup = (key, filter) => (
    <div key={key} className="mb-6">
      <h4 className="font-medium text-sm text-gray-700 mb-3">{filter.label}</h4>
      <div className="space-y-2">
        {filter.options.map((option) => (
          <label 
            key={option.value} 
            className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded"
          >
            <Checkbox
              checked={selectedFilters[key]?.includes(option.value) || false}
              onCheckedChange={(checked) => {
                const current = selectedFilters[key] || [];
                const updated = checked
                  ? [...current, option.value]
                  : current.filter(v => v !== option.value);
                onFilterChange(key, updated);
              }}
            />
            <span className="text-sm text-gray-600">{option.label}</span>
          </label>
        ))}
      </div>
    </div>
  );

  const activeFilterCount = Object.values(selectedFilters).flat().length + 
    (salaryRange?.min || salaryRange?.max ? 1 : 0) +
    (locationFilter ? 1 : 0) +
    (postedWithin ? 1 : 0);

  // Popular locations by country
  const popularLocations = {
    SL: ['Colombo', 'Kandy', 'Galle', 'Negombo', 'Jaffna', 'Batticaloa'],
    UK: ['London', 'Manchester', 'Birmingham', 'Leeds', 'Bristol', 'Edinburgh'],
  };

  // Posted within options
  const postedOptions = [
    { value: '24h', label: 'Last 24 hours' },
    { value: '3d', label: 'Last 3 days' },
    { value: '7d', label: 'Last 7 days' },
    { value: '14d', label: 'Last 14 days' },
    { value: '30d', label: 'Last 30 days' },
  ];

  return (
    <div className={isMobile ? '' : 'sticky top-4'}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
          <SlidersHorizontal className="h-4 w-4" />
          Filters
          {activeFilterCount > 0 && (
            <Badge variant="secondary" className="text-xs">{activeFilterCount}</Badge>
          )}
        </h3>
        {activeFilterCount > 0 && (
          <Button variant="ghost" size="sm" onClick={onClear} className="text-gray-500">
            <X className="h-4 w-4 mr-1" />
            Clear
          </Button>
        )}
      </div>

      {/* Salary Range Filter */}
      <div className="mb-6">
        <h4 className="font-medium text-sm text-gray-700 mb-3 flex items-center gap-2">
          <DollarSign className="h-4 w-4" />
          Salary Range
        </h4>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs text-gray-500">Min</Label>
              <Input
                type="number"
                placeholder={`${currency?.symbol || 'Rs'} Min`}
                value={salaryMin}
                onChange={(e) => setSalaryMin(e.target.value)}
                className="h-9 text-sm"
              />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Max</Label>
              <Input
                type="number"
                placeholder={`${currency?.symbol || 'Rs'} Max`}
                value={salaryMax}
                onChange={(e) => setSalaryMax(e.target.value)}
                className="h-9 text-sm"
              />
            </div>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            className="w-full"
            onClick={handleSalaryApply}
          >
            Apply Salary Filter
          </Button>
          {(salaryRange?.min || salaryRange?.max) && (
            <div className="flex items-center justify-between text-xs text-gray-500">
              <span>
                {salaryRange.min ? `${currency?.symbol}${salaryRange.min.toLocaleString()}` : 'Any'} 
                {' - '}
                {salaryRange.max ? `${currency?.symbol}${salaryRange.max.toLocaleString()}` : 'Any'}
              </span>
              <button 
                onClick={() => {
                  setSalaryMin('');
                  setSalaryMax('');
                  onSalaryChange({ min: null, max: null });
                }}
                className="text-blue-600 hover:underline"
              >
                Clear
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Location Filter */}
      <div className="mb-6">
        <h4 className="font-medium text-sm text-gray-700 mb-3 flex items-center gap-2">
          <MapPin className="h-4 w-4" />
          Location
        </h4>
        <Input
          type="text"
          placeholder="City or area..."
          value={locationFilter || ''}
          onChange={(e) => onLocationChange(e.target.value)}
          className="h-9 text-sm mb-2"
        />
        <div className="flex flex-wrap gap-1">
          {(popularLocations[currency?.code === 'GBP' ? 'UK' : 'SL'] || popularLocations.SL).slice(0, 4).map((loc) => (
            <Badge 
              key={loc}
              variant="outline" 
              className={`cursor-pointer text-xs ${locationFilter === loc ? 'bg-blue-50 border-blue-300' : ''}`}
              onClick={() => onLocationChange(locationFilter === loc ? '' : loc)}
            >
              {loc}
            </Badge>
          ))}
        </div>
      </div>

      {/* Posted Within Filter */}
      <div className="mb-6">
        <h4 className="font-medium text-sm text-gray-700 mb-3 flex items-center gap-2">
          <Clock className="h-4 w-4" />
          Posted Within
        </h4>
        <div className="space-y-2">
          {postedOptions.map((option) => (
            <label 
              key={option.value} 
              className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded"
            >
              <Checkbox
                checked={postedWithin === option.value}
                onCheckedChange={(checked) => onPostedWithinChange(checked ? option.value : '')}
              />
              <span className="text-sm text-gray-600">{option.label}</span>
            </label>
          ))}
        </div>
      </div>

      <Separator className="my-4" />
      
      {Object.entries(filters).map(([key, filter]) => renderFilterGroup(key, filter))}
    </div>
  );
};

// Main Jobs Page Component
const JobsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { country, formatCurrency } = useCountry();
  const { toast } = useToast();
  
  // State
  const [jobs, setJobs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filters, setFilters] = useState({});
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [currency, setCurrency] = useState({ symbol: 'Rs', code: 'LKR' });
  const [totalJobs, setTotalJobs] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Search and filter state
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');
  const [selectedFilters, setSelectedFilters] = useState({});
  const [sortBy, setSortBy] = useState('newest');
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  
  // New advanced filter state
  const [salaryRange, setSalaryRange] = useState({ min: null, max: null });
  const [locationFilter, setLocationFilter] = useState(searchParams.get('location') || '');
  const [postedWithin, setPostedWithin] = useState('');
  const [quickFilters, setQuickFilters] = useState([]);
  
  // Saved searches state
  const [savedSearches, setSavedSearches] = useState([]);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [saveSearchName, setSaveSearchName] = useState('');
  const [saveAlertEnabled, setSaveAlertEnabled] = useState(true);
  const [saveAlertFrequency, setSaveAlertFrequency] = useState('daily');
  const [savingSearch, setSavingSearch] = useState(false);
  const [loadingSavedSearches, setLoadingSavedSearches] = useState(false);
  
  // Service Nudges - integrated across all categories
  const nudges = useServiceNudges(selectedCategory || 'default');

  // Get user ID from localStorage
  const getUserId = () => localStorage.getItem('userId') || localStorage.getItem('user_id');
  const getUserEmail = () => localStorage.getItem('userEmail') || localStorage.getItem('user_email');

  // Fetch saved searches
  const fetchSavedSearches = useCallback(async () => {
    const userId = getUserId();
    if (!userId) return;
    
    try {
      setLoadingSavedSearches(true);
      const response = await axios.get(`${API_URL}/api/jobs/saved-searches`, {
        headers: { 'X-User-ID': userId }
      });
      if (response.data.saved_searches) {
        setSavedSearches(response.data.saved_searches);
      }
    } catch (error) {
      console.error('Error fetching saved searches:', error);
    } finally {
      setLoadingSavedSearches(false);
    }
  }, []);

  // Save current search
  const handleSaveSearch = async () => {
    const userId = getUserId();
    const userEmail = getUserEmail();
    
    if (!userId) {
      toast({
        title: "Login Required",
        description: "Please login to save searches",
        variant: "destructive"
      });
      return;
    }
    
    if (!saveSearchName.trim()) {
      toast({
        title: "Name Required",
        description: "Please enter a name for your saved search",
        variant: "destructive"
      });
      return;
    }
    
    try {
      setSavingSearch(true);
      
      // Build filters object
      const currentFilters = {
        country,
        search: searchQuery || undefined,
        category: selectedCategory || undefined,
        location: locationFilter || undefined,
        salary_min: salaryRange.min || undefined,
        salary_max: salaryRange.max || undefined,
        work_style: quickFilters.includes('remote') ? 'remote' : selectedFilters.work_style?.[0],
        urgent_only: quickFilters.includes('urgent') || undefined,
        featured_only: quickFilters.includes('featured') || undefined,
        ...Object.fromEntries(
          Object.entries(selectedFilters).map(([k, v]) => [k, v?.[0]])
        ),
      };
      
      // Remove undefined values
      Object.keys(currentFilters).forEach(key => {
        if (currentFilters[key] === undefined) delete currentFilters[key];
      });
      
      const response = await axios.post(
        `${API_URL}/api/jobs/saved-searches`,
        {
          name: saveSearchName,
          filters: currentFilters,
          email_alerts: saveAlertEnabled,
          alert_frequency: saveAlertFrequency,
        },
        {
          headers: {
            'X-User-ID': userId,
            'X-User-Email': userEmail || '',
          }
        }
      );
      
      if (response.data.success) {
        toast({
          title: "Search Saved!",
          description: saveAlertEnabled 
            ? `You'll receive ${saveAlertFrequency} email alerts for new matching jobs`
            : "You can run this search anytime from the dropdown",
        });
        setShowSaveDialog(false);
        setSaveSearchName('');
        fetchSavedSearches();
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to save search",
        variant: "destructive"
      });
    } finally {
      setSavingSearch(false);
    }
  };

  // Load a saved search
  const loadSavedSearch = async (search) => {
    const filters = search.filters || {};
    
    // Apply filters
    setSearchQuery(filters.search || '');
    setSelectedCategory(filters.category || '');
    setLocationFilter(filters.location || '');
    setSalaryRange({
      min: filters.salary_min || null,
      max: filters.salary_max || null,
    });
    
    // Apply quick filters
    const newQuickFilters = [];
    if (filters.work_style === 'remote') newQuickFilters.push('remote');
    if (filters.urgent_only) newQuickFilters.push('urgent');
    if (filters.featured_only) newQuickFilters.push('featured');
    setQuickFilters(newQuickFilters);
    
    // Apply other filters
    const newSelectedFilters = {};
    ['employment_type', 'schedule', 'pay_type', 'experience_level'].forEach(key => {
      if (filters[key]) newSelectedFilters[key] = [filters[key]];
    });
    setSelectedFilters(newSelectedFilters);
    
    setCurrentPage(1);
    
    toast({
      title: `Loaded: ${search.name}`,
      description: "Filters applied from saved search",
    });
  };

  // Delete a saved search
  const deleteSavedSearch = async (searchId) => {
    const userId = getUserId();
    if (!userId) return;
    
    try {
      await axios.delete(`${API_URL}/api/jobs/saved-searches/${searchId}`, {
        headers: { 'X-User-ID': userId }
      });
      toast({
        title: "Search Deleted",
        description: "Saved search has been removed",
      });
      fetchSavedSearches();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete saved search",
        variant: "destructive"
      });
    }
  };

  // Toggle alerts for a saved search
  const toggleSearchAlerts = async (searchId, currentStatus) => {
    const userId = getUserId();
    if (!userId) return;
    
    try {
      await axios.put(
        `${API_URL}/api/jobs/saved-searches/${searchId}`,
        { email_alerts: !currentStatus },
        { headers: { 'X-User-ID': userId } }
      );
      toast({
        title: currentStatus ? "Alerts Disabled" : "Alerts Enabled",
        description: currentStatus 
          ? "You won't receive email alerts for this search"
          : "You'll receive email alerts for new matching jobs",
      });
      fetchSavedSearches();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update alert settings",
        variant: "destructive"
      });
    }
  };

  // Fetch categories and filters
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [categoriesRes, filtersRes, statsRes] = await Promise.all([
          axios.get(`${API_URL}/api/jobs/categories`),
          axios.get(`${API_URL}/api/jobs/filters`),
          axios.get(`${API_URL}/api/jobs/stats?country=${country}`),
        ]);
        
        if (categoriesRes.data.categories) {
          setCategories(categoriesRes.data.categories);
        }
        if (filtersRes.data.filters) {
          setFilters(filtersRes.data.filters);
        }
        if (statsRes.data.stats) {
          setStats(statsRes.data.stats);
        }
      } catch (error) {
        console.error('Error fetching initial data:', error);
      }
    };
    
    fetchInitialData();
    fetchSavedSearches(); // Fetch saved searches on mount
  }, [country, fetchSavedSearches]);

  // Fetch jobs
  const fetchJobs = useCallback(async () => {
    try {
      setLoading(true);
      
      const params = {
        country,
        page: currentPage,
        limit: 20,
        sort_by: sortBy,
      };
      
      if (searchQuery) params.search = searchQuery;
      if (selectedCategory) params.category = selectedCategory;
      
      // Add selected filters to params
      Object.entries(selectedFilters).forEach(([key, values]) => {
        if (values && values.length > 0) {
          params[key] = values[0]; // API expects single value for most filters
        }
      });
      
      // Add salary range filter
      if (salaryRange.min) params.salary_min = salaryRange.min;
      if (salaryRange.max) params.salary_max = salaryRange.max;
      
      // Add location filter
      if (locationFilter) params.location = locationFilter;
      
      // Add posted within filter (convert to days for backend)
      if (postedWithin) {
        const daysMap = { '24h': 1, '3d': 3, '7d': 7, '14d': 14, '30d': 30 };
        params.posted_days = daysMap[postedWithin] || 30;
      }
      
      // Add quick filters
      if (quickFilters.includes('urgent')) params.urgent_only = true;
      if (quickFilters.includes('featured')) params.featured_only = true;
      if (quickFilters.includes('remote')) params.work_style = 'remote';
      
      const response = await axios.get(`${API_URL}/api/jobs/listings`, { params });
      
      if (response.data.jobs) {
        setJobs(response.data.jobs);
        setTotalJobs(response.data.total);
        setTotalPages(response.data.total_pages);
        setCurrency(response.data.currency);
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  }, [country, currentPage, sortBy, searchQuery, selectedCategory, selectedFilters, salaryRange, locationFilter, postedWithin, quickFilters]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    const params = new URLSearchParams(searchParams);
    if (searchQuery) {
      params.set('search', searchQuery);
    } else {
      params.delete('search');
    }
    setSearchParams(params);
  };

  // Handle category change
  const handleCategoryChange = (slug) => {
    setSelectedCategory(slug);
    setCurrentPage(1);
    const params = new URLSearchParams(searchParams);
    if (slug) {
      params.set('category', slug);
    } else {
      params.delete('category');
    }
    setSearchParams(params);
  };

  // Handle filter change
  const handleFilterChange = (key, values) => {
    setSelectedFilters(prev => ({
      ...prev,
      [key]: values,
    }));
    setCurrentPage(1);
  };

  // Clear all filters
  const clearFilters = () => {
    setSelectedFilters({});
    setSelectedCategory('');
    setSearchQuery('');
    setSalaryRange({ min: null, max: null });
    setLocationFilter('');
    setPostedWithin('');
    setQuickFilters([]);
    setCurrentPage(1);
    setSearchParams({});
  };

  // Toggle quick filter
  const toggleQuickFilter = (filter) => {
    setQuickFilters(prev => 
      prev.includes(filter) 
        ? prev.filter(f => f !== filter)
        : [...prev, filter]
    );
    setCurrentPage(1);
  };

  // Navigate to job detail
  const handleJobClick = (job) => {
    navigate(`/jobs/${job.id}`);
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="jobs-page">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white py-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-2 text-blue-200 text-sm mb-2">
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="h-4 w-4" />
            <span className="text-white">Jobs</span>
          </div>
          
          <h1 className="text-3xl font-bold mb-2">💼 Find Your Next Job</h1>
          <p className="text-blue-100 mb-4">
            {totalJobs.toLocaleString()} jobs available{selectedCategory && ` in ${categories.find(c => c.slug === selectedCategory)?.name || selectedCategory}`}
          </p>
          
          {/* Search Bar with Location */}
          <form onSubmit={handleSearch} className="flex gap-2 flex-wrap">
            <div className="flex-1 min-w-[250px] relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Job title, company, or skills..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-12 bg-white text-gray-900"
                data-testid="job-search-input"
              />
            </div>
            <div className="w-48 relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Location..."
                value={locationFilter}
                onChange={(e) => setLocationFilter(e.target.value)}
                className="pl-10 h-12 bg-white text-gray-900"
                data-testid="location-filter-input"
              />
            </div>
            <Button type="submit" size="lg" className="bg-white text-blue-600 hover:bg-blue-50">
              Search
            </Button>
          </form>
          
          {/* Quick Filter Chips */}
          <div className="flex flex-wrap gap-2 mt-4">
            <Badge 
              variant={quickFilters.includes('remote') ? 'default' : 'outline'}
              className={`cursor-pointer transition-all ${quickFilters.includes('remote') ? 'bg-white text-blue-600' : 'bg-white/20 text-white border-white/40 hover:bg-white/30'}`}
              onClick={() => toggleQuickFilter('remote')}
              data-testid="quick-filter-remote"
            >
              <Laptop className="h-3 w-3 mr-1" />
              Remote
            </Badge>
            <Badge 
              variant={quickFilters.includes('urgent') ? 'default' : 'outline'}
              className={`cursor-pointer transition-all ${quickFilters.includes('urgent') ? 'bg-white text-blue-600' : 'bg-white/20 text-white border-white/40 hover:bg-white/30'}`}
              onClick={() => toggleQuickFilter('urgent')}
              data-testid="quick-filter-urgent"
            >
              <Zap className="h-3 w-3 mr-1" />
              Urgent
            </Badge>
            <Badge 
              variant={quickFilters.includes('featured') ? 'default' : 'outline'}
              className={`cursor-pointer transition-all ${quickFilters.includes('featured') ? 'bg-white text-blue-600' : 'bg-white/20 text-white border-white/40 hover:bg-white/30'}`}
              onClick={() => toggleQuickFilter('featured')}
              data-testid="quick-filter-featured"
            >
              <Star className="h-3 w-3 mr-1" />
              Featured
            </Badge>
            {selectedFilters.work_style?.includes('hybrid') || (
              <Badge 
                variant="outline"
                className="cursor-pointer bg-white/20 text-white border-white/40 hover:bg-white/30"
                onClick={() => handleFilterChange('work_style', ['hybrid'])}
              >
                <Home className="h-3 w-3 mr-1" />
                Hybrid
              </Badge>
            )}
            
            {/* Saved Searches Dropdown & Save Button */}
            <div className="flex items-center gap-2 ml-auto">
              {savedSearches.length > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="bg-white/10 border-white/30 text-white hover:bg-white/20"
                    >
                      <Bookmark className="h-4 w-4 mr-1" />
                      Saved ({savedSearches.length})
                      <ChevronDown className="h-4 w-4 ml-1" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-72">
                    <div className="px-2 py-1.5 text-sm font-medium text-gray-500">
                      My Saved Searches
                    </div>
                    <DropdownMenuSeparator />
                    {savedSearches.map((search) => (
                      <div key={search.id} className="flex items-center justify-between px-2 py-1.5 hover:bg-gray-50 rounded">
                        <button 
                          onClick={() => loadSavedSearch(search)}
                          className="flex-1 text-left"
                        >
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm">{search.name}</span>
                            {search.new_jobs_count > 0 && (
                              <Badge variant="destructive" className="text-xs px-1.5 py-0">
                                {search.new_jobs_count} new
                              </Badge>
                            )}
                          </div>
                          <div className="text-xs text-gray-500">
                            {search.email_alerts ? (
                              <span className="flex items-center gap-1">
                                <Bell className="h-3 w-3" />
                                {search.alert_frequency} alerts
                              </span>
                            ) : (
                              <span className="flex items-center gap-1">
                                <BellOff className="h-3 w-3" />
                                No alerts
                              </span>
                            )}
                          </div>
                        </button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => loadSavedSearch(search)}>
                              <Play className="h-4 w-4 mr-2" />
                              Run Search
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => toggleSearchAlerts(search.id, search.email_alerts)}>
                              {search.email_alerts ? (
                                <>
                                  <BellOff className="h-4 w-4 mr-2" />
                                  Disable Alerts
                                </>
                              ) : (
                                <>
                                  <Bell className="h-4 w-4 mr-2" />
                                  Enable Alerts
                                </>
                              )}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              onClick={() => deleteSavedSearch(search.id)}
                              className="text-red-600"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              
              <Button 
                variant="outline" 
                size="sm"
                className="bg-white/10 border-white/30 text-white hover:bg-white/20"
                onClick={() => setShowSaveDialog(true)}
                data-testid="save-search-btn"
              >
                <BookmarkPlus className="h-4 w-4 mr-1" />
                Save Search
              </Button>
            </div>
          </div>
          
          {/* Quick Stats */}
          {stats && (
            <div className="flex gap-6 mt-4 text-sm">
              <span className="flex items-center gap-1">
                <Zap className="h-4 w-4 text-yellow-300" />
                {stats.urgent_jobs} urgent
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="h-4 w-4 text-green-300" />
                {stats.new_jobs_24h} new today
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex gap-6">
          {/* Desktop Sidebar */}
          <div className="hidden lg:block w-64 flex-shrink-0">
            {/* Categories */}
            <Card className="mb-4">
              <CardHeader className="py-3">
                <CardTitle className="text-sm font-medium">Categories</CardTitle>
              </CardHeader>
              <CardContent className="py-0 pb-3">
                <div className="space-y-1">
                  <button
                    onClick={() => handleCategoryChange('')}
                    className={`w-full text-left px-2 py-1.5 rounded text-sm transition-colors ${
                      !selectedCategory ? 'bg-blue-50 text-blue-700 font-medium' : 'hover:bg-gray-50'
                    }`}
                  >
                    All Jobs
                  </button>
                  {categories.map((cat) => (
                    <button
                      key={cat.slug}
                      onClick={() => handleCategoryChange(cat.slug)}
                      className={`w-full text-left px-2 py-1.5 rounded text-sm transition-colors flex items-center gap-2 ${
                        selectedCategory === cat.slug ? 'bg-blue-50 text-blue-700 font-medium' : 'hover:bg-gray-50'
                      }`}
                    >
                      <span>{cat.icon}</span>
                      <span className="truncate">{cat.name.replace(' Jobs', '')}</span>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
            
            {/* Filters */}
            <Card>
              <CardContent className="py-4">
                <FilterSidebar
                  filters={filters}
                  selectedFilters={selectedFilters}
                  onFilterChange={handleFilterChange}
                  onClear={clearFilters}
                  salaryRange={salaryRange}
                  onSalaryChange={setSalaryRange}
                  locationFilter={locationFilter}
                  onLocationChange={setLocationFilter}
                  postedWithin={postedWithin}
                  onPostedWithinChange={setPostedWithin}
                  currency={currency}
                />
              </CardContent>
            </Card>
          </div>

          {/* Job Listings */}
          <div className="flex-1 min-w-0">
            {/* Mobile Filter Button & Sort */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Sheet open={showMobileFilters} onOpenChange={setShowMobileFilters}>
                  <SheetTrigger asChild>
                    <Button variant="outline" size="sm" className="lg:hidden">
                      <Filter className="h-4 w-4 mr-2" />
                      Filters
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="left" className="w-80">
                    <SheetHeader>
                      <SheetTitle>Filter Jobs</SheetTitle>
                    </SheetHeader>
                    <div className="mt-4 max-h-[80vh] overflow-y-auto">
                      <FilterSidebar
                        filters={filters}
                        selectedFilters={selectedFilters}
                        onFilterChange={handleFilterChange}
                        onClear={clearFilters}
                        salaryRange={salaryRange}
                        onSalaryChange={setSalaryRange}
                        locationFilter={locationFilter}
                        onLocationChange={setLocationFilter}
                        postedWithin={postedWithin}
                        onPostedWithinChange={setPostedWithin}
                        currency={currency}
                        isMobile
                      />
                    </div>
                  </SheetContent>
                </Sheet>
                
                {/* Active filters summary */}
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm text-gray-500">
                    {totalJobs.toLocaleString()} results
                  </span>
                  {quickFilters.length > 0 && (
                    <div className="flex gap-1">
                      {quickFilters.map(f => (
                        <Badge key={f} variant="secondary" className="text-xs">
                          {f}
                          <X 
                            className="h-3 w-3 ml-1 cursor-pointer" 
                            onClick={() => toggleQuickFilter(f)}
                          />
                        </Badge>
                      ))}
                    </div>
                  )}
                  {(salaryRange.min || salaryRange.max) && (
                    <Badge variant="secondary" className="text-xs">
                      Salary: {salaryRange.min ? `${currency.symbol}${salaryRange.min.toLocaleString()}` : 'Any'} - {salaryRange.max ? `${currency.symbol}${salaryRange.max.toLocaleString()}` : 'Any'}
                      <X 
                        className="h-3 w-3 ml-1 cursor-pointer" 
                        onClick={() => setSalaryRange({ min: null, max: null })}
                      />
                    </Badge>
                  )}
                  {locationFilter && (
                    <Badge variant="secondary" className="text-xs">
                      📍 {locationFilter}
                      <X 
                        className="h-3 w-3 ml-1 cursor-pointer" 
                        onClick={() => setLocationFilter('')}
                      />
                    </Badge>
                  )}
                </div>
              </div>
              
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-40" data-testid="sort-select">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest">Newest First</SelectItem>
                  <SelectItem value="oldest">Oldest First</SelectItem>
                  <SelectItem value="salary_high">Salary: High to Low</SelectItem>
                  <SelectItem value="salary_low">Salary: Low to High</SelectItem>
                  <SelectItem value="deadline">Application Deadline</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Job Cards */}
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                <span className="ml-2 text-gray-600">Loading jobs...</span>
              </div>
            ) : jobs.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Briefcase className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No jobs found</h3>
                  <p className="text-gray-500 mb-4">Try adjusting your search or filters</p>
                  <Button variant="outline" onClick={clearFilters}>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Clear Filters
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Job Career Services Section - Shows for supported categories */}
                {currentPage === 1 && (
                  <>
                    <JobCareerServicesSection jobCategory={selectedCategory} />
                    {/* Service Purchase Sheet for bundles */}
                    {searchParams.get('show_bundles') === 'true' && selectedCategory && (
                      <div className="mb-4">
                        <JobServicePurchaseSheet jobCategory={selectedCategory}>
                          <Button className="w-full bg-green-600 hover:bg-green-700" data-testid="bundle-purchase-btn">
                            <Package className="h-4 w-4 mr-2" />
                            View Bundle Packages & Save
                          </Button>
                        </JobServicePurchaseSheet>
                      </div>
                    )}
                  </>
                )}
                
                <div className="space-y-3">
                  {/* Nudge Banner - shows after threshold views */}
                  {nudges.currentNudge && (
                    <NudgeBanner
                      nudge={nudges.currentNudge}
                      onDismiss={nudges.dismissNudge}
                      onAction={nudges.handleNudgeAction}
                    />
                  )}
                  
                  {jobs.map((job, index) => (
                    <React.Fragment key={job.id}>
                      <JobCard
                        job={job}
                        currency={currency}
                        onClick={() => {
                          nudges.trackView();
                          handleJobClick(job);
                        }}
                      />
                      
                      {/* Inline nudge after every 4th job (positions 3, 7, 11...) */}
                      {nudges.getInlineNudge(index) && (
                        <InlineNudgeCard
                          nudge={nudges.getInlineNudge(index)}
                          onDismiss={nudges.dismissInlineNudge}
                          onAction={nudges.handleNudgeAction}
                        />
                      )}
                    </React.Fragment>
                  ))}
                </div>
                
                {/* Toast nudge for mobile */}
                {nudges.currentNudge && window.innerWidth < 768 && (
                  <NudgeToast
                    nudge={nudges.currentNudge}
                    onDismiss={nudges.dismissNudge}
                    onAction={nudges.handleNudgeAction}
                  />
                )}
              </>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-6">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(p => p - 1)}
                >
                  Previous
                </Button>
                <span className="text-sm text-gray-600">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(p => p + 1)}
                >
                  Next
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Save Search Dialog */}
      <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookmarkPlus className="h-5 w-5 text-blue-600" />
              Save This Search
            </DialogTitle>
            <DialogDescription>
              Save your current filters and get notified when new matching jobs are posted.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="search-name">Search Name</Label>
              <Input
                id="search-name"
                placeholder="e.g., Remote Tech Jobs in Colombo"
                value={saveSearchName}
                onChange={(e) => setSaveSearchName(e.target.value)}
                className="mt-1"
              />
            </div>
            
            {/* Current Filters Summary */}
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-sm font-medium text-gray-700 mb-2">Current Filters:</p>
              <div className="flex flex-wrap gap-1">
                {searchQuery && <Badge variant="secondary" className="text-xs">"{searchQuery}"</Badge>}
                {selectedCategory && <Badge variant="secondary" className="text-xs">{selectedCategory}</Badge>}
                {locationFilter && <Badge variant="secondary" className="text-xs">📍 {locationFilter}</Badge>}
                {quickFilters.map(f => <Badge key={f} variant="secondary" className="text-xs">{f}</Badge>)}
                {(salaryRange.min || salaryRange.max) && (
                  <Badge variant="secondary" className="text-xs">
                    {currency.symbol}{salaryRange.min || 0} - {currency.symbol}{salaryRange.max || '∞'}
                  </Badge>
                )}
                {!searchQuery && !selectedCategory && !locationFilter && quickFilters.length === 0 && !salaryRange.min && !salaryRange.max && (
                  <span className="text-sm text-gray-500">All jobs in {country}</span>
                )}
              </div>
            </div>
            
            {/* Email Alerts Toggle */}
            <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
              <div className="flex items-center gap-2">
                <Bell className="h-5 w-5 text-blue-600" />
                <div>
                  <p className="font-medium text-sm">Email Alerts</p>
                  <p className="text-xs text-gray-600">Get notified when new jobs match</p>
                </div>
              </div>
              <Switch
                checked={saveAlertEnabled}
                onCheckedChange={setSaveAlertEnabled}
              />
            </div>
            
            {/* Alert Frequency */}
            {saveAlertEnabled && (
              <div>
                <Label>Alert Frequency</Label>
                <Select value={saveAlertFrequency} onValueChange={setSaveAlertFrequency}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="instant">Instant (as jobs are posted)</SelectItem>
                    <SelectItem value="daily">Daily Digest</SelectItem>
                    <SelectItem value="weekly">Weekly Summary</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSaveDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveSearch} disabled={savingSearch}>
              {savingSearch ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Bookmark className="h-4 w-4 mr-2" />
                  Save Search
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default JobsPage;
